import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Client, Region, TopicWeight, Story, SeedBalance } from '@/types/database'

/**
 * Admin views over the fleet.
 *
 * The old model had one brief per client per day, so "did today's brief
 * generate" was the operational signal. Under on-demand generation there is no
 * daily brief, and the thing an operator actually wants to see is whether a
 * client is using their seeds and whether their coverage is configured.
 */

export interface ClientSummary extends Client {
  seedsRemaining: number | null
  seedsWeek: number | null
  regionsUsed: number
  totalStories: number
  onboarded: boolean
}

export function useAdminClientList() {
  return useQuery({
    queryKey: ['admin', 'client-list'],
    queryFn: async () => {
      const clientsRes = await supabase.from('clients').select('*').order('display_name')
      const clients = (clientsRes.data ?? []) as Client[]
      if (clients.length === 0) return []

      const [regionCounts, storyCounts, balances] = await Promise.all([
        Promise.all(clients.map((c) =>
          supabase.from('regions').select('*', { count: 'exact', head: true })
            .eq('client_id', c.id).eq('active', true)
        )),
        // Counted in the database: never pull story rows just to length them.
        Promise.all(clients.map((c) =>
          supabase.from('stories').select('*', { count: 'exact', head: true }).eq('client_id', c.id)
        )),
        Promise.all(clients.map((c) => supabase.rpc('seed_balance', { p_client_id: c.id }))),
      ])

      return clients.map((c, i): ClientSummary => {
        const bal = (balances[i].data as SeedBalance[] | null)?.[0] ?? null
        return {
          ...c,
          seedsRemaining: bal?.total_available ?? null,
          seedsWeek: bal?.seeds_week ?? null,
          regionsUsed: regionCounts[i].count ?? 0,
          totalStories: storyCounts[i].count ?? 0,
          onboarded: !!c.onboarded_at,
        }
      })
    },
    refetchInterval: 30_000,
  })
}

export interface ClientDetail {
  client: Client
  regions: Region[]
  topicWeights: TopicWeight[]
  balance: SeedBalance | null
  recentStories: Pick<Story, 'id' | 'headline' | 'category' | 'status' | 'created_at'>[]
}

export function useAdminClient(clientId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'client', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const [clientRes, regionsRes, topicWeightsRes, storiesRes, balanceRes] = await Promise.all([
        supabase.from('clients').select('*').eq('id', clientId!).single(),
        supabase.from('regions').select('*').eq('client_id', clientId!).order('name'),
        supabase.from('topic_weights').select('*').eq('client_id', clientId!)
          .order('weight', { ascending: false }),
        supabase.from('stories').select('id, headline, category, status, created_at')
          .eq('client_id', clientId!).order('created_at', { ascending: false }).limit(20),
        supabase.rpc('seed_balance', { p_client_id: clientId! }),
      ])

      return {
        client: clientRes.data as Client,
        regions: (regionsRes.data ?? []) as Region[],
        topicWeights: (topicWeightsRes.data ?? []) as TopicWeight[],
        balance: (balanceRes.data as SeedBalance[] | null)?.[0] ?? null,
        recentStories: (storiesRes.data ?? []) as ClientDetail['recentStories'],
      } satisfies ClientDetail
    },
  })
}
